import { Button, Callout } from "@nodus/ui";
import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { AuthCard } from "../../../app/auth-card";
import { useRuntime } from "../../../app/runtime";
import { cliKeyName, loopbackURL, parseCliAuth } from "../../../features/console/cli-auth";
import { useCliOrgs } from "../../../features/console/cli-orgs";
import { OrgPicker } from "../../../features/console/org-picker";

// CLI login approval (ADR-081, ui-spec §6.20). Keys are minted per chosen org by POST /auth/cli-keys; with no org
// card-backed onboarding opens separately, preserving the pending CLI challenge in this tab.
export const Route = createFileRoute("/_authed/cli-auth/")({
  validateSearch: (s: Record<string, unknown>) => s,
  component: CliAuth,
});

function CliAuth() {
  const req = parseCliAuth(Route.useSearch());
  const { api } = useRuntime();
  const { orgs, loaded, selected, setChosen, gate, refresh } = useCliOrgs();
  const [state, setState] = React.useState<"ask" | "sending" | "done" | "cancelled">("ask");
  const [error, setError] = React.useState<string>();

  if ("problem" in req) {
    return <AuthCard title="This login link is incomplete" description={`${req.problem} Run nodus login again.`} />;
  }
  if (state === "done") return <AuthCard title="You're signed in" description="You can close this tab and return to your terminal." />;
  if (state === "cancelled") return <AuthCard title="Login cancelled" description="Nothing was created. You can close this tab." />;
  if (gate) return gate;

  const name = cliKeyName(req.host, new Date());
  async function authorize() {
    if ("problem" in req) return;
    setState("sending");
    setError(undefined);
    try {
      const r = await api.createCliKeys({ orgs: selected, codeChallenge: req.codeChallenge, state: req.state, name });
      setState("done");
      window.location.assign(loopbackURL(req, r));
    } catch (err) {
      setState("ask");
      setError(err instanceof Error ? err.message : "Could not authorize the CLI.");
      // A refusal often means the orgs changed elsewhere (an invite accepted, an org created): retry on fresh ones.
      void refresh();
    }
  }

  return (
    <AuthCard title="Authorize the Nodus CLI" description={<>Sign in the CLI on <code className="font-mono text-fg-body">{req.host}</code>.</>}>
      <div className="grid gap-5">
        {orgs?.length ? (
          <OrgPicker orgs={orgs} chosen={selected} onChange={setChosen} />
        ) : loaded ? (
          <Callout tone="info"><a className="underline" href="/onboarding" target="_blank" rel="noopener noreferrer">Verify your card and create your organization</a>, then return here and refresh your organizations. Your CLI login stays open in this tab.
            <Button variant="outline" onClick={() => void refresh()}>Refresh organizations</Button></Callout>
        ) : null}
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-ui">
          <dt className="text-fg-muted">Key name</dt>
          <dd className="font-mono text-fg-secondary">{name}</dd>
          <dt className="text-fg-muted">Access</dt>
          <dd className="text-fg-secondary">Full access · expires in 90 days</dd>
        </dl>
        {error ? <p role="alert" className="text-ui text-danger-fg">{error}</p> : null}
        <div className="flex gap-2">
          <Button onClick={() => void authorize()} disabled={state === "sending" || !loaded || !orgs?.length || selected.length === 0}>
            Authorize
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              if ("problem" in req) return;
              setState("cancelled");
              window.location.assign(loopbackURL(req, { error: "access_denied" }));
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </AuthCard>
  );
}
